import { type JSX, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { useMaintenanceBypassAccess } from "../hooks/useMaintenanceBypassAccess";
import MaintenanceDowntimePage from "../pages/MaintenanceDowntimePage";
import { useMaintenance } from "../state/MaintenanceContext";

import MaintenanceNotificationBanner from "./MaintenanceNotificationBanner";

import { ROUTES } from "@/app/constants/routes_sidebar/routes.constant";

interface MaintenanceRootGateProps {
  children: ReactNode;
}

/**
 * Renders global maintenance notification and blocks app access during maintenance.
 *
 * @param props Application content.
 * @returns Maintenance-aware root content.
 */
const MaintenanceRootGate = (props: MaintenanceRootGateProps): JSX.Element => {
  const { children } = props;
  const { pathname } = useLocation();
  const canBypassMaintenance = useMaintenanceBypassAccess();
  const { schedule, isActive, shouldNotify } = useMaintenance();
  const isMaintenanceAdminRoute = pathname === ROUTES.MAINTENANCE;

  const shouldShowDowntimePage =
    isActive && !(canBypassMaintenance && isMaintenanceAdminRoute);

  if (shouldShowDowntimePage) {
    return <MaintenanceDowntimePage schedule={schedule} />;
  }

  return (
    <>
      {shouldNotify && schedule && (
        <MaintenanceNotificationBanner schedule={schedule} />
      )}
      {children}
    </>
  );
};

export default MaintenanceRootGate;
